import { version } from "../meta.json";
import { createStore, action, actionOn, computed } from "easy-peasy";
import {
  sortGrades,
  sortQueuesByName,
  sortShifts,
  sortLocations,
  sortTeams,
  sortSchedules
} from "./utils";

const model = {
  /**
   * Loading overlay
   */
  loading: {
    visibility: false,
    message: "",
    setLoading: action((state, payload) => {
      state.visibility = payload.visibility;
      state.message = payload.message;
    })
  },

  /**
   * Notify
   */
  notifier: {
    data: [],
    notify: action((state, { key, message, type, ...payload }) => {
      state.data = [
        ...state.data,
        {
          key: key ? key : new Date().getTime() + Math.random(),
          message: message,
          options: { variant: type, ...payload }
        }
      ];
    }),
    close: action((state, payload) => {
      state.data.map((item, index) => {
        // closeSnackbar() without key dismisses all.
        return !payload || item.key === payload
          ? (state.data[index] = { ...item, dismissed: true })
          : (state.data[index] = { ...item });
      });
    }),
    remove: action((state, payload) => {
      state.data = state.data.filter(
        notifications => notifications.key !== payload
      );
    })
  },

  /**
   * Authentication
   */
  auth: {
    authenticated: false,
    authenticate: action((state, payload) => {
      state.authenticated = payload;
    })
  },

  /**
   * Supervisor
   */
  supervisor: {
    id: null,
    name: null,
    departmentId: null,
    departmentName: null,
    departmentCode: null,
    location: null,
    shift: null,
    schedule: null,
    twilioAccessToken: null,
    twilioClientToken: null,
    set: action((state, payload) => {
      state.id = payload.id;
      state.name = payload.name;
      state.departmentId = payload.department_id;
      state.departmentName = payload.department_name;
      state.departmentCode = payload.department_code;
      state.location = payload.location;
      state.shift = payload.shift;
      state.schedule = payload.schedule;
      state.twilioAccessToken = payload.twilio_access_token;
      state.twilioVideoToken = payload.twilio_video_token;
    }),
    station: window.HOSTNAME
      ? window.HOSTNAME.split(".")[0].toUpperCase()
      : "N/A",
    version: version ? version.split(".")[0] : null,
    build: window.ELECTRON_BUILD ? window.ELECTRON_BUILD : null
  },

  permissions: {
    data: {},
    set: action((state, payload) => {
      state.data.observeAll = payload.observe_all;
    })
  },

  /**
   * Departments
   */
  departments: {
    data: {},
    setData: action((state, payload) => {
      let data = {};
      payload.map(item => (data[parseInt(item.id)] = item));
      state.data = data;
    }),
    selected: {},
    setSelected: action((state, payload) => {
      state.selected = payload;
    }),
    onSetSelected: actionOn(
      actions => actions.setSelected,
      (state, target) => {
        window.localStorage.setItem("departmentId", target.payload.id);
        window.localStorage.setItem("departmentCode", target.payload.code);
      }
    )
  },

  /**
   * Queue management state and actions.
   */
  queues: {
    /**
     * Data object holding the queue information.
     * @type {Object}
     */
    data: {},

    /**
     * Action to add or update queues based on events.
     * @param {Object} state - The current state of the queues.
     * @param {Object} payload - The payload containing items, event, code, and stats.
     * @param {Object} payload.items - The items to be added.
     * @param {string} payload.event - The event type.
     * @param {string} payload.code - The code for the queue item.
     * @param {Object} payload.stats - The statistics for the queue item.
     */
    addQueues: action((state, { items, event, code, stats }) => {
      switch (event) {
        case "allItemsAdded":
          // Add all items to the data (already sorted)
          state.data = items;
          break;

        case "itemAdded":
          // Add a single item to the data and re-sort
          if (Object.keys(state.data).length > 0) {
            state.data[code] = stats;
          } else {
            state.data = { [code]: stats };
          }
          state.data = sortQueuesByName(state.data);
          break;

        case "itemUpdated":
          // Update an existing item in the data
          if (Object.keys(state.data).length > 0) {
            state.data[code] = stats;
          }
          state.data = sortQueuesByName(state.data);
          break;

        case "itemRemoved":
          // Remove an item from the data
          if (Object.keys(state.data).length > 0) {
            delete state.data[code];
          }
          break;

        case "reset":
          // Reset the data to an empty object
          state.data = {};
          break;

        default:
          break;
      }
    }),

    /**
     * Loading state.
     * @type {boolean}
     */
    loading: false,

    /**
     * Action to set the loading state.
     * @param {Object} state - The current state of the queues.
     * @param {boolean} payload - The new loading state.
     */
    setLoading: action((state, payload) => {
      state.loading = payload;
    }),

    totalDrops: computed(state =>
      Object.values(state.data).reduce(
        (sum, item) => sum + (item.calls_dropped || 0),
        0
      )
    ),

    totalDropPercent: computed(state => {
      const totalEnqueued = Object.values(state.data).reduce(
        (sum, item) => sum + (item.calls_enqueued || 0),
        0
      );

      const percentage =
        totalEnqueued > 0 ? (state.totalDrops / totalEnqueued) * 100 : 0;
      return Math.round(percentage);
    }),

    callsWaiting: computed(state =>
      Object.values(state.data).reduce(
        (sum, item) => sum + (parseInt(item.calls_waiting) || 0),
        0
      )
    ),

    callsWaitingMaxTime: computed(state => {
      const waitTimes = Object.values(state.data).map(
        item => parseInt(item.calls_waiting_max_time) || 0
      );
      return waitTimes.length > 0 ? Math.max(...waitTimes) : 0;
    })
  },

  /**
   * Agents
   */
  agents: {
    data: {},
    setData: action((state, { event, key, data }) => {
      switch (event) {
        case "added":
          state.data = data;
          break;
        case "itemAdded":
          state.data = state.data[key]
            ? state.data
            : { ...state.data, [key]: data };
          break;
        case "itemUpdated":
          state.data[key] = data;
          break;
        case "reset":
          state.data = {};
          break;
        default:
          break;
      }
    }),

    reset: action(state => {
      state.data = {};
    }),

    online: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity !== "Offline" && item.grade !== "M"
        ).length
    ),
    talking: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity === "Talking" && item.grade !== "M"
        ).length
    ),
    wrapUp: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity === "Wrap-Up" && item.grade !== "M"
        ).length
    ),
    ready: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity === "Ready" && item.grade !== "M"
        ).length
    ),
    notReady: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity === "Not Ready" && item.grade !== "M"
        ).length
    ),
    meeting: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity === "Meeting" && item.grade !== "M"
        ).length
    ),
    observing: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity === "Observing" && item.grade !== "M"
        ).length
    ),
    break: computed(
      state =>
        Object.values(state.data).filter(
          item =>
            ["Break 15", "Break 30", "Break 45", "Break 60"].includes(
              item.activity
            ) && item.grade !== "M"
        ).length
    ),
    offline: computed(
      state =>
        Object.values(state.data).filter(
          item => item.activity === "Offline" && item.grade !== "M"
        ).length
    ),

    selectedAgent: false,
    setSelectedAgent: action((state, payload) => {
      state.selectedAgent = payload;
    }),
    selectedAgentData: computed(state =>
      state.selectedAgent ? state.data[state.selectedAgent] : null
    ),

    totalSales: computed(state =>
      Object.values(state.data).reduce(
        (sum, item) => sum + (item.sales || 0),
        0
      )
    ),

    totalCalls: computed(state =>
      Object.values(state.data).reduce(
        (sum, item) => sum + (item.calls || 0),
        0
      )
    ),

    totalSalesPercent: computed(state => {
      return state.totalCalls > 0
        ? (state.totalSales / state.totalCalls) * 100
        : 0;
    }),

    avgTalkTime: computed(state => {
      const agents = Object.values(state.data);

      if (agents.length === 0) return 0;

      const sumOfAverages = agents.reduce(
        (sum, item) => sum + (item.calls ? item.talkTime / item.calls : 0),
        0
      );

      return sumOfAverages / agents.length;
    }),

    avgReadyTime: computed(state => {
      const agents = Object.values(state.data);

      if (agents.length === 0) return 0;

      const sumOfAverages = agents.reduce(
        (sum, item) => sum + (item.calls ? item.readyTime / item.calls : 0),
        0
      );

      return sumOfAverages / agents.length;
    }),

    avgWrapTime: computed(state => {
      const agents = Object.values(state.data);

      if (agents.length === 0) return 0;

      const sumOfAverages = agents.reduce(
        (sum, item) => sum + (item.calls ? item.wrapTime / item.calls : 0),
        0
      );

      return sumOfAverages / agents.length;
    }),

    inactivePercent: computed(state => {
      return state.online > 0
        ? Math.round(((state.notReady + state.break) / state.online) * 100)
        : 0;
    }),

    groupedByGrade: computed(state => {
      const groups = {};
      Object.values(state.data).forEach(agent => {
        const key = agent.grade || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(agent);
      });
      return groups;
    }),

    groupedByTeam: computed(state => {
      const groups = {};
      Object.values(state.data).forEach(agent => {
        const key = agent.team || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(agent);
      });
      return groups;
    }),

    groupedByLocation: computed(state => {
      const groups = {};
      Object.values(state.data).forEach(agent => {
        const key = agent.location || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(agent);
      });
      return groups;
    }),

    groupedByShift: computed(state => {
      const groups = {};
      Object.values(state.data).forEach(agent => {
        const key = agent.shift || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(agent);
      });
      return groups;
    }),

    groupedBySchedule: computed(state => {
      const groups = {};
      Object.values(state.data).forEach(agent => {
        const key = agent.schedule || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(agent);
      });
      return groups;
    }),

    groupedByQueue: computed(state => {
      const groups = {};
      Object.values(state.data).forEach(agent => {
        const key = agent.queue || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(agent);
      });
      return groups;
    })
  },

  grouping: {
    selected: null, // null means default view, otherwise 'grade', 'team', etc.
    setSelected: action((state, payload) => {
      state.selected = payload;
    })
  },

  refresh: {
    value: 0,
    trigger: action(state => {
      state.value += 1;
    })
  },

  /**
   * User settings (theme mode, etc.). Persisted to localStorage and mirrored to
   * the <html data-theme> attribute so CSS variables flip. Seeded from
   * localStorage on load; defaults to "dark".
   */
  settings: {
    themeMode: window.localStorage.getItem("themeMode") || "dark",
    setThemeMode: action((state, payload) => {
      state.themeMode = payload;
    }),
    onSetThemeMode: actionOn(
      actions => actions.setThemeMode,
      (state, target) => {
        window.localStorage.setItem("themeMode", target.payload);
        document.documentElement.setAttribute("data-theme", target.payload);
      }
    )
  }
};

export const store = createStore(model);
